import axios, {AxiosError} from 'axios';
import fileDownload from 'js-file-download';
import {toast} from 'react-toastify';
import {call, ForkEffect, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ActionCreators} from 'redux-undo';
import i18n, {type TypedTranslation} from '../../i18n';
import {IPresentation} from '../../incase-domain/presentation';
import {
    downloadPresentationVerbose,
    fetchPresentationVerbose,
    getPresentationProtection,
    updatePresentationProtection,
} from '../../service/presentation';
import {fetchPresentationThemeService} from '../../service/presentationTheme.service';
import {addSlides, copyPresentationSlides, CopyPresentationSlidesResult, saveSlideSwap} from '../../service/slides';
import createSlideTemplate from '../../service/template/template';
import {ADD_SLIDES, SWAP_SELECTED_SLIDES} from '../actions/edit';
import {
    DELETE_EMITTED_SHARED_PRESENTATION_ACTION,
    EXPORT_PRESENTATION,
    exportPresentationEndAction,
    FETCH_PRESENTATION_PROTECTION,
    SELECT_PRESENTATION_TO_EDIT,
    setDeletedEmittedSharedPresentationAction,
    setEmittedSharedPresentationAction,
    UPDATE_PRESENTATION_PROTECTION,
    UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION,
    UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION,
    updateSelectedPresentationAction,
    updateSelectedPresentationSlideIndexListAction,
    updateSelectedPresentationSlidesAction,
    updateSettingsConfigAction,
} from '../actions/presentation';
import {CREATE_SLIDES_COPY, updateSelectedSlideAction} from '../actions/slide';
import {
    FETCH_PRESENTATION_SLIDE_PREVIEWS,
    hideLoadingSpinnerAction,
    showLoadingSpinnerAction,
    updateSelectedSlidePreviewsAction,
} from '../actions/slide.preview';
import {getSlidePreviews} from '../../service/slide.preview';
import {mergeSlidePreviewIntoList, mergeSlidePreviewLists, withSlidePreviewCacheBust} from '../../utils/slidePreviewState';
import {startPageSpinnerAction, startPresUpdateSpinnerAction, stopPageSpinnerAction, stopPresUpdateSpinnerAction} from '../actions/spinner';
import {IAddSlidesAction, ISwapSelectedSlidesAction} from '../actions/types/edit.types';
import {
    IDeleteEmittedSharedPresentationAction,
    IExportPresentationAction,
    IFetchPresentationProtectionAction,
    ISelectPresentationToEditAction,
    IUpdatePresentationProtection,
    IUpdatePresentationWithEmittedPresentationAction,
    IUpdateSharedPresentationWithEmittedSharedPresentationAction,
} from '../actions/types/presentation.types';
import {ICreateSlidesCopyAction} from '../actions/types/slide.types';
import {QUERY_KEYS} from '../../utils/queryKeys';
import {queryClient} from '../../utils/queryClient';
import {AppState} from '../reducer';
import {ISlidePreview} from '../../incase-domain/proptypes/slidePreview';

export function* selectPresentationToEditSaga(action: ISelectPresentationToEditAction): unknown {
    const {presentationId, template, navigate} = action.payload;

    yield put(startPageSpinnerAction());

    try {
        const presentationToEdit: IPresentation = yield call(fetchPresentationVerbose, presentationId);
        const presentationToEditSlides = presentationToEdit.slides || [];

        yield put(updateSelectedPresentationAction({...presentationToEdit, slides: []}));
        yield put(updateSelectedPresentationSlidesAction(presentationToEditSlides));
        yield put(updateSelectedPresentationSlidesAction(presentationToEditSlides));

        if (template) {
            const slide = presentationToEdit.slides[0];
            const slideWithTemplate = {
                ...presentationToEdit.slides[0],
                elements: [...slide.elements, ...createSlideTemplate(template, slide._id)],
            };
            yield put(updateSelectedPresentationSlidesAction([slide]));

            yield put(updateSelectedSlideAction(slideWithTemplate, 0, false));
        } else {
            yield put(updateSelectedSlideAction(presentationToEdit.slides[0], 0, false));
        }

        yield put(showLoadingSpinnerAction());
        const slidePreviews: ISlidePreview[] = yield call(getSlidePreviews, presentationToEdit.slideIndexList);
        yield put(updateSelectedSlidePreviewsAction((slidePreviews || []).map(withSlidePreviewCacheBust)));
        yield put(hideLoadingSpinnerAction());
        yield put(ActionCreators.clearHistory());

        if (presentationToEdit.presentationThemeId) {
            const themeId = presentationToEdit.presentationThemeId;
            yield call(() =>
                queryClient.prefetchQuery({
                    queryKey: QUERY_KEYS.theme.detail(themeId),
                    queryFn: () => fetchPresentationThemeService(themeId),
                })
            );
        }
    } catch (e) {
        if (axios.isAxiosError(e)) {
            const axiosError: AxiosError = e;
            if (
                axiosError.response &&
                typeof axiosError.response.data === 'object' &&
                axiosError.response.data !== null &&
                'message' in axiosError.response.data
            ) {
                toast.warning(i18n.t(axiosError.response.data.message as TypedTranslation));
            }
        }

        navigate('/');
    }

    yield put(stopPageSpinnerAction());
}

export function* exportPresentationSaga(action: IExportPresentationAction): unknown {
    try {
        const response = yield call(downloadPresentationVerbose, action.presentationId);
        const exportName = decodeURIComponent(response.headers['x-filename']);
        fileDownload(response.data, exportName);
        toast.success(`${i18n.t('toasts.presentation')} ${exportName} ${i18n.t('toasts.exportPresentation')}`);
        yield put(exportPresentationEndAction(action.presentationId));
    } catch (e) {
        toast.error(i18n.t('toasts.errorOccurred'));
        yield put(exportPresentationEndAction(action.presentationId));
    }
}

export function* saveSwapSlidesSaga(action: ISwapSelectedSlidesAction): unknown {
    const {newIndex, oldIndex} = action.indices;

    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    try {
        const updatedPresentation: IPresentation = yield call(saveSlideSwap, selectedPresentation._id, newIndex, oldIndex);

        if (selectedSlideIndex === oldIndex) {
            put(updateSelectedSlideAction(selectedSlide, newIndex, false));
        } else if (newIndex <= selectedSlideIndex && oldIndex > selectedSlideIndex) {
            put(updateSelectedSlideAction(selectedSlide, selectedSlideIndex + 1, false));
        } else if (newIndex >= selectedSlideIndex && oldIndex < selectedSlideIndex) {
            put(updateSelectedSlideAction(selectedSlide, selectedSlideIndex - 1, false));
        }

        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                slideIndexList: updatedPresentation.slideIndexList,
            })
        );
    } catch (e) {
        toast.success(i18n.t('toasts.errorOccurred'));
    }
}

export function* addSlidesSaga(action: IAddSlidesAction): unknown {
    const {slidesToAdd, hidden, template, toEnd} = action;
    yield put(startPresUpdateSpinnerAction(true));

    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlideIndex} = state.slideReducer.present;

    const newSlidePosition = action.toEnd ? selectedPresentation.slideIndexList.length + 1 : selectedSlideIndex + 1;

    const presentation: IPresentation = yield call(addSlides, selectedPresentation._id, slidesToAdd, newSlidePosition, hidden);

    if (presentation.newSlides) {
        const newSlide = presentation.newSlides[0];
        const newSlideWithTemplate = {
            ...newSlide,
            elements: [...newSlide.elements, ...createSlideTemplate(template, newSlide._id)],
        };

        const updatedSlides = selectedPresentation.slides;
        updatedSlides.splice(newSlidePosition, 0, ...presentation.newSlides);
        yield put(updateSelectedPresentationSlidesAction(updatedSlides));
        yield put(updateSelectedPresentationSlideIndexListAction(presentation.slideIndexList));
        yield put(
            updateSelectedSlideAction(newSlideWithTemplate, toEnd ? presentation.slideIndexList.length - 1 : selectedSlideIndex + 1, true)
        );
    }

    yield put(stopPresUpdateSpinnerAction());
}

export function* createSlidesCopySaga(action: ICreateSlidesCopyAction): unknown {
    try {
        yield put(startPresUpdateSpinnerAction(true));
        const state: AppState = yield select();
        const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;

        const result: CopyPresentationSlidesResult | undefined = yield call(
            copyPresentationSlides,
            selectedPresentation._id,
            action.slides.map((s) => s._id)
        );

        if (result?.copies?.length) {
            const copiedSlides = result.copies.map((copy) => copy.slide);
            const allSlides = [...selectedPresentation.slides, ...copiedSlides];
            const newSlides = result.presentation.slideIndexList
                .map((id) => allSlides.find((s) => s._id === id))
                .filter((s): s is NonNullable<typeof s> => !!s);

            const previews = result.copies.reduce(
                (list, copy) =>
                    copy.slidePreview?.imgSrc
                        ? mergeSlidePreviewIntoList(list, withSlidePreviewCacheBust({...copy.slidePreview, slideId: copy.slide._id}))
                        : list,
                selectedPresentationSlidePreviews
            );

            yield put(updateSelectedPresentationAction({...result.presentation, slides: newSlides}));
            yield put(updateSelectedSlidePreviewsAction(previews));

            const lastCopy = copiedSlides[copiedSlides.length - 1];
            yield put(
                updateSelectedSlideAction(
                    lastCopy,
                    newSlides.findIndex((s) => s._id === lastCopy._id),
                    false
                )
            );
        }
        yield put(stopPresUpdateSpinnerAction());
    } catch (error) {
        yield put(stopPresUpdateSpinnerAction());
    }
}

export function* updatePresentationProtectionSaga(action: IUpdatePresentationProtection): unknown {
    yield put(startPresUpdateSpinnerAction(true));

    try {
        yield call(updatePresentationProtection, action.presentationId, action.protection);
        yield put(updateSettingsConfigAction(action.protection));
        if (!action.silent) {
            yield call(toast.success, i18n.t('toasts.successfullySaved'));
        }
    } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 400) {
            const responseData = e.response.data;
            let messageKey = (<AxiosError<{message: string}>>e).response?.data.message as string;

            if (responseData && typeof responseData === 'object') {
                const firstProp = Object.values(responseData)[0];
                if (Array.isArray(firstProp) && firstProp.length > 0) {
                    [messageKey] = firstProp;
                }
            }

            yield call(toast.error, i18n.t(messageKey as TypedTranslation));
        } else {
            yield call(toast.error, i18n.t('toasts.notSavedTryAgain'));
        }
    } finally {
        yield put(stopPresUpdateSpinnerAction());
    }
}

export function* fetchPresentationProtectionSaga(action: IFetchPresentationProtectionAction): unknown {
    yield put(startPresUpdateSpinnerAction(true));
    const protection = yield call(getPresentationProtection, action.presentationId);
    yield put(updateSettingsConfigAction(protection));
    yield put(stopPresUpdateSpinnerAction());
}

export function* updatePresentationWithEmittedPresentationSaga(action: IUpdatePresentationWithEmittedPresentationAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    if (action.presentation) {
        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                ...action.presentation,
                slides: selectedPresentation.slides,
            })
        );
    }
    if (action.labels) {
        yield put(
            updateSelectedPresentationAction({
                ...selectedPresentation,
                languageConfig: {labels: action.labels},
            })
        );
    }
    if (action.protection) {
        yield put(updateSettingsConfigAction(action.protection));
    }
}

export function* updateSharedPresentationWithEmittedSharedPresentationSaga(
    action: IUpdateSharedPresentationWithEmittedSharedPresentationAction
): unknown {
    if (action.sharedPresentation) {
        yield put(setEmittedSharedPresentationAction(action.sharedPresentation));
    }
}

export function* deleteEmittedSharedPresentationSaga(action: IDeleteEmittedSharedPresentationAction): unknown {
    if (action.presentationIds) {
        yield put(setDeletedEmittedSharedPresentationAction(action.presentationIds));
    }
}

export function* fetchPresentationSlidePreviewsSaga(): unknown {
    const state: AppState = yield select();
    const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;
    const slideIds = selectedPresentation.slideIndexList;
    if (!slideIds?.length) {
        return;
    }

    try {
        const slidePreviews: ISlidePreview[] | undefined = yield call(getSlidePreviews, slideIds);
        if (!slidePreviews) {
            return;
        }
        yield put(
            updateSelectedSlidePreviewsAction(
                mergeSlidePreviewLists(selectedPresentationSlidePreviews, slidePreviews.map(withSlidePreviewCacheBust))
            )
        );
    } catch {
        // Keep the last known thumbnails if the refetch fails.
    }
}

export function* watchPresentationSaga(): Generator<ForkEffect<never>, void> {
    yield takeLatest(SELECT_PRESENTATION_TO_EDIT, selectPresentationToEditSaga);
    yield takeEvery(EXPORT_PRESENTATION, exportPresentationSaga);
    yield takeLatest(SWAP_SELECTED_SLIDES, saveSwapSlidesSaga);
    yield takeLatest(ADD_SLIDES, addSlidesSaga);
    yield takeLatest(CREATE_SLIDES_COPY, createSlidesCopySaga);
    yield takeLatest(UPDATE_PRESENTATION_PROTECTION, updatePresentationProtectionSaga);
    yield takeLatest(FETCH_PRESENTATION_PROTECTION, fetchPresentationProtectionSaga);
    yield takeLatest(UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION, updatePresentationWithEmittedPresentationSaga);
    yield takeLatest(
        UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION,
        updateSharedPresentationWithEmittedSharedPresentationSaga
    );
    yield takeLatest(DELETE_EMITTED_SHARED_PRESENTATION_ACTION, deleteEmittedSharedPresentationSaga);
    yield takeLatest(FETCH_PRESENTATION_SLIDE_PREVIEWS, fetchPresentationSlidePreviewsSaga);
}
